import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Trophy, Medal, Award, Heart, MessageCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface TopMember {
  user_id: string;
  nickname: string;
  avatar_url?: string;
  total_likes: number;
  total_comments: number;
  activity_score: number;
  rank: number;
}

interface TopMembersRankingProps {
  communityId: string;
  refreshTrigger?: number; // Para forçar refresh quando há novas interações
}

const TopMembersRanking: React.FC<TopMembersRankingProps> = ({ communityId, refreshTrigger }) => {
  const [topMembers, setTopMembers] = useState<TopMember[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (communityId) {
      fetchTopMembers();
    }
  }, [communityId, refreshTrigger]); // Adicionado refreshTrigger como dependência

  const fetchTopMembers = async () => {
    try {
      console.log('🏆 [TopMembersRanking] Buscando top membros para comunidade:', communityId);
      
      // Query SQL complexa para calcular ranking de atividade
      const { data, error } = await supabase.rpc('get_community_top_members', {
        p_community_id: communityId,
        p_limit: 3
      });

      if (error) {
        console.error('❌ [TopMembersRanking] Erro na function:', error);
        // Fallback: buscar dados manualmente
        await fetchTopMembersManual();
        return;
      }

      console.log('✅ [TopMembersRanking] Top membros encontrados:', data);
      setTopMembers(data || []);
      
    } catch (error) {
      console.error('❌ [TopMembersRanking] Erro ao buscar top membros:', error);
      // Fallback para método manual
      await fetchTopMembersManual();
    } finally {
      setLoading(false);
    }
  };

  const fetchTopMembersManual = async () => {
    try {
      console.log('🔄 [TopMembersRanking] Usando método manual...');
      
      // 1. Buscar todos os posts da comunidade
      const { data: posts, error: postsError } = await supabase
        .from('community_posts')
        .select('id, author_id')
        .eq('community_id', communityId);

      if (postsError) throw postsError;

      if (!posts || posts.length === 0) {
        setTopMembers([]);
        return;
      }

      const postIds = posts.map(p => p.id);
      const memberActivities: Record<string, { likes: number; comments: number }> = {};

      // 2. Inicializar contadores para autores dos posts
      posts.forEach(post => {
        if (!memberActivities[post.author_id]) {
          memberActivities[post.author_id] = { likes: 0, comments: 0 };
        }
      });

      // 3. Buscar likes usando community_post_likes
      const { data: likes, error: likesError } = await supabase
        .from('community_post_likes')
        .select('user_id, post_id')
        .in('post_id', postIds);

      if (!likesError && likes) {
        console.log('✅ [TopMembersRanking] Likes carregados:', likes.length);
        likes.forEach(like => {
          if (!memberActivities[like.user_id]) {
            memberActivities[like.user_id] = { likes: 0, comments: 0 };
          }
          memberActivities[like.user_id].likes += 1;
        });
      } else {
        console.warn('⚠️ [TopMembersRanking] Erro ao carregar likes:', likesError);
      }

      // 4. Buscar comentários usando community_post_comments
      const { data: comments, error: commentsError } = await supabase
        .from('community_post_comments')
        .select('author_id, post_id')
        .in('post_id', postIds);

      if (!commentsError && comments) {
        console.log('✅ [TopMembersRanking] Comentários carregados:', comments.length);
        comments.forEach(comment => {
          if (!memberActivities[comment.author_id]) {
            memberActivities[comment.author_id] = { likes: 0, comments: 0 };
          }
          memberActivities[comment.author_id].comments += 1;
        });
      } else {
        console.warn('⚠️ [TopMembersRanking] Erro ao carregar comentários:', commentsError);
      }

      // 5. Calcular scores e ordenar
      const membersWithScores = Object.entries(memberActivities)
        .map(([userId, activity]) => ({
          user_id: userId,
          total_likes: activity.likes,
          total_comments: activity.comments,
          activity_score: activity.likes + activity.comments // Peso igual
        }))
        .filter(member => member.activity_score > 0)
        .sort((a, b) => b.activity_score - a.activity_score)
        .slice(0, 3);

      // 6. Buscar dados dos perfis dos usuários individualmente usando as tabelas corretas
      if (membersWithScores.length > 0) {
        const profilePromises = membersWithScores.map(async (member) => {
          try {
            console.log(`👤 [TopMembersRanking] Processando membro: ${member.user_id}`);
            
            // Primeiro buscar o role na tabela profiles
            const { data: profileData, error: profileError } = await supabase
              .from('profiles')
              .select('id, nickname, role')
              .eq('id', member.user_id)
              .single();

            if (profileError || !profileData) {
              console.warn(`⚠️ [TopMembersRanking] Perfil não encontrado para: ${member.user_id}`, profileError);
              return {
                ...member,
                nickname: 'Usuário',
                avatar_url: null,
                rank: 0
              };
            }

            console.log(`✅ [TopMembersRanking] Perfil encontrado: ${profileData.nickname} (${profileData.role})`);

            let imageUrl = null;
            let nickname = profileData.nickname || 'Usuário';

            // Buscar imagem baseado no role - mas de forma mais robusta
            if (profileData.role === 'mentor') {
              console.log(`🔍 [TopMembersRanking] Buscando dados do mentor: ${member.user_id}`);
              try {
                // Usar uma consulta mais simples primeiro
                const { data: mentorData, error: mentorError } = await supabase
                  .from('mentor_info')
                  .select('*')
                  .eq('profile_id', member.user_id)
                  .limit(1);

                console.log(`📋 [TopMembersRanking] Resultado mentor query:`, { mentorData, mentorError });

                if (!mentorError && mentorData && mentorData.length > 0) {
                  const mentor = mentorData[0];
                  
                  // Usar nome do mentor se disponível
                  if (mentor.name) {
                    nickname = mentor.name;
                  }
                  
                  // Processar URL da imagem do mentor
                  if (mentor.image_url) {
                    try {
                      if (mentor.image_url.startsWith('http')) {
                        imageUrl = mentor.image_url;
                      } else {
                        // Se é apenas o nome do arquivo, formar caminho completo
                        let fullPath = mentor.image_url;
                        if (!mentor.image_url.includes('/')) {
                          fullPath = `${member.user_id}/${mentor.image_url}`;
                        }
                        
                        const { data: urlData } = supabase.storage
                          .from('profile-images')
                          .getPublicUrl(fullPath);
                        imageUrl = urlData.publicUrl;
                      }
                      console.log(`✅ [TopMembersRanking] Imagem do mentor processada: ${imageUrl}`);
                    } catch (imgError) {
                      console.warn(`⚠️ [TopMembersRanking] Erro ao processar imagem do mentor:`, imgError);
                    }
                  }
                  
                  console.log(`✅ [TopMembersRanking] Dados do mentor encontrados: ${nickname}`);
                } else {
                  console.log(`⚠️ [TopMembersRanking] Mentor info não encontrada para: ${member.user_id}`, mentorError);
                }
              } catch (mentorError) {
                console.warn(`⚠️ [TopMembersRanking] Erro ao buscar mentor:`, mentorError);
              }
            } else if (profileData.role === 'jogador') {
              console.log(`🔍 [TopMembersRanking] Buscando dados do jogador: ${member.user_id}`);
              try {
                // Usar uma consulta mais simples primeiro
                const { data: playerData, error: playerError } = await supabase
                  .from('player_social_media')
                  .select('*')
                  .eq('profile_id', member.user_id)
                  .limit(1);

                console.log(`📋 [TopMembersRanking] Resultado player query:`, { playerData, playerError });

                if (!playerError && playerData && playerData.length > 0 && playerData[0].image_url) {
                  const player = playerData[0];
                  
                  // Processar URL da imagem do jogador
                  try {
                    if (player.image_url.startsWith('http')) {
                      imageUrl = player.image_url;
                    } else {
                      // Se é apenas o nome do arquivo, formar caminho completo
                      let fullPath = player.image_url;
                      if (!player.image_url.includes('/')) {
                        fullPath = `${member.user_id}/${player.image_url}`;
                      }
                      
                      const { data: urlData } = supabase.storage
                        .from('profile-images')
                        .getPublicUrl(fullPath);
                      imageUrl = urlData.publicUrl;
                    }
                    console.log(`✅ [TopMembersRanking] Imagem do jogador processada: ${imageUrl}`);
                  } catch (imgError) {
                    console.warn(`⚠️ [TopMembersRanking] Erro ao processar imagem do jogador:`, imgError);
                  }
                  
                  console.log(`✅ [TopMembersRanking] Dados do jogador encontrados: ${nickname}`);
                } else {
                  console.log(`⚠️ [TopMembersRanking] Player social media não encontrada para: ${member.user_id}`, playerError);
                }
              } catch (playerError) {
                console.warn(`⚠️ [TopMembersRanking] Erro ao buscar jogador:`, playerError);
              }
            }

            return {
              ...member,
              nickname,
              avatar_url: imageUrl,
              rank: 0
            };
          } catch (error) {
            console.error(`❌ [TopMembersRanking] Erro ao buscar perfil ${member.user_id}:`, error);
            return {
              ...member,
              nickname: 'Usuário',
              avatar_url: null,
              rank: 0
            };
          }
        });

        const topMembersWithProfiles = await Promise.all(profilePromises);
        
        // Adicionar ranks após buscar os perfis
        const rankedMembers = topMembersWithProfiles.map((member, index) => ({
          ...member,
          rank: index + 1
        }));

        console.log('✅ [TopMembersRanking] Top membros (manual):', rankedMembers);
        setTopMembers(rankedMembers);
      } else {
        setTopMembers([]);
      }

    } catch (error) {
      console.error('❌ [TopMembersRanking] Erro no método manual:', error);
      setTopMembers([]);
    }
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Trophy className="h-4 w-4 text-yellow-500" />;
      case 2:
        return <Medal className="h-4 w-4 text-gray-400" />;
      case 3:
        return <Award className="h-4 w-4 text-amber-600" />;
      default:
        return null;
    }
  };

  const getRankColor = (rank: number) => {
    switch (rank) {
      case 1:
        return "bg-gradient-to-r from-yellow-500/20 to-yellow-600/20 border-yellow-500/30";
      case 2:
        return "bg-gradient-to-r from-gray-400/20 to-gray-500/20 border-gray-400/30";
      case 3:
        return "bg-gradient-to-r from-amber-600/20 to-amber-700/20 border-amber-600/30";
      default:
        return "bg-[#1A2333] border-[#222A35]";
    }
  };

  if (loading) {
    return (
      <Card className="bg-[#1A2333] border-[#222A35]">
        <CardHeader>
          <CardTitle className="text-white text-base lg:text-lg">
            🏆 Membros Mais Ativos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-[#0F1419]/50 animate-pulse">
                <div className="w-8 h-8 bg-gray-600 rounded-full"></div>
                <div className="flex-1">
                  <div className="w-24 h-3 bg-gray-600 rounded mb-1"></div>
                  <div className="w-16 h-2 bg-gray-600 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (topMembers.length === 0) {
    return (
      <Card className="bg-[#1A2333] border-[#222A35]">
        <CardHeader>
          <CardTitle className="text-white text-base lg:text-lg">
            🏆 Membros Mais Ativos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <Trophy className="h-8 w-8 text-gray-500 mx-auto mb-2" />
            <p className="text-sm text-gray-400">
              Ainda não há atividade suficiente para criar um ranking.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-[#1A2333] border-[#222A35]">
      <CardHeader>
        <CardTitle className="text-white text-base lg:text-lg flex items-center gap-2">
          <Trophy className="h-5 w-5 text-yellow-500" />
          Membros Mais Ativos
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {topMembers.map((member) => (
            <div
              key={member.user_id}
              className={`flex items-center gap-3 p-3 rounded-lg border ${getRankColor(member.rank)} transition-all duration-200 hover:scale-[1.02]`}
            >
              {/* Rank Icon */}
              <div className="flex-shrink-0">
                {getRankIcon(member.rank)}
              </div>

              {/* Avatar */}
              <Avatar className="h-8 w-8">
                <AvatarImage src={member.avatar_url} alt={member.nickname} />
                <AvatarFallback className="bg-[#00B6F0] text-black text-xs">
                  {member.nickname.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>

              {/* User Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-white text-sm font-medium truncate">
                    {member.nickname}
                  </p>
                  <Badge 
                    variant="secondary" 
                    className="bg-[#00B6F0]/20 text-[#00B6F0] text-xs px-1.5 py-0.5"
                  >
                    #{member.rank}
                  </Badge>
                </div>
                
                {/* Activity Stats */}
                <div className="flex items-center gap-3 mt-1">
                  <div className="flex items-center gap-1 text-xs text-gray-400">
                    <Heart className="h-3 w-3" />
                    <span>{member.total_likes}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-400">
                    <MessageCircle className="h-3 w-3" />
                    <span>{member.total_comments}</span>
                  </div>
                  <div className="text-xs text-[#00B6F0] font-medium">
                    {member.activity_score} pts
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Explicação do ranking */}
        <div className="mt-4 pt-3 border-t border-[#222A35]">
          <p className="text-xs text-gray-500 text-center">
            Ranking baseado em likes e comentários na comunidade
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default TopMembersRanking; 